import { useEffect, useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { cn } from './lib/cn'
import { Spinner } from './primitives'

/* ==========================================================================
 * Theme-aware palette
 * ======================================================================== */

export interface ChartTokens {
  accent: string
  text: string
  subtle: string
  border: string
  surface: string
  grid: string
  success: string
  warning: string
  danger: string
  info: string
}

function readToken(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(`--ms-${name}`)
    .trim()
  return raw ? `rgb(${raw})` : fallback
}

function readTokens(): ChartTokens {
  return {
    accent: readToken('accent', '#4B5262'),
    text: readToken('text', '#08090C'),
    subtle: readToken('text-subtle', '#8A91A0'),
    border: readToken('border', '#E3E6ED'),
    surface: readToken('surface', '#FFFFFF'),
    grid: readToken('border', '#E3E6ED'),
    success: readToken('success', '#72D6A0'),
    warning: readToken('warning', '#E8B86D'),
    danger: readToken('error', '#EF8585'),
    info: readToken('info', '#8BADEB'),
  }
}

/**
 * Chart colours resolved from the design tokens.
 *
 * Recharts writes colours as SVG presentation attributes, and `var()` does not
 * resolve there — only in style properties. So the values are read from the
 * computed custom properties instead, and re-read whenever `data-theme`
 * changes so a theme switch repaints the charts.
 */
export function useChartTokens(): ChartTokens {
  const [tokens, setTokens] = useState<ChartTokens>(readTokens)

  useEffect(() => {
    const refresh = () => setTokens(readTokens())
    refresh()

    const observer = new MutationObserver(refresh)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    })
    return () => observer.disconnect()
  }, [])

  return tokens
}

/* ==========================================================================
 * ChartCard
 * ======================================================================== */

export interface ChartCardProps {
  title: string
  description?: React.ReactNode
  action?: React.ReactNode
  /** Fixed height keeps the chart from collapsing before it measures. */
  height?: number
  loading?: boolean
  className?: string
  children: React.ReactNode
}

export function ChartCard({
  title,
  description,
  action,
  height = 260,
  loading = false,
  className,
  children,
}: ChartCardProps) {
  return (
    <section className={cn('rounded-card border border-line bg-surface p-5 shadow-card', className)}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {description ? <p className="text-caption text-muted">{description}</p> : null}
        </div>
        {action}
      </header>

      <div className="mt-4" style={{ height }}>
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner className="h-5 w-5 text-subtle" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {children as React.ReactElement}
          </ResponsiveContainer>
        )}
      </div>
    </section>
  )
}

/* ==========================================================================
 * Shared axis / tooltip config
 * ======================================================================== */

function useAxisProps() {
  const tokens = useChartTokens()
  return useMemo(
    () => ({
      tokens,
      axis: {
        stroke: tokens.subtle,
        tick: { fill: tokens.subtle, fontSize: 11 },
        tickLine: false,
        axisLine: false,
      },
      grid: { stroke: tokens.grid, strokeDasharray: '3 3', vertical: false },
    }),
    [tokens]
  )
}

interface TooltipEntry {
  name?: string | number
  value?: string | number
  color?: string
  dataKey?: string | number
}

function ChartTooltip({
  active,
  payload,
  label,
  tokens,
  suffix,
}: {
  active?: boolean
  payload?: TooltipEntry[]
  label?: string | number
  tokens: ChartTokens
  suffix?: string
}) {
  if (!active || !payload?.length) return null

  return (
    <div
      className="rounded-lg border px-2.5 py-2 shadow-raised"
      style={{ background: tokens.surface, borderColor: tokens.border }}
    >
      {label !== undefined ? (
        <p className="mb-1 font-mono text-[11px]" style={{ color: tokens.subtle }}>
          {label}
        </p>
      ) : null}
      {payload.map((entry, index) => (
        <p key={index} className="flex items-center gap-2 text-caption" style={{ color: tokens.text }}>
          <span
            aria-hidden
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: entry.color }}
          />
          <span>{entry.name ?? entry.dataKey}</span>
          <span className="ml-auto font-mono font-medium">
            {entry.value}
            {suffix}
          </span>
        </p>
      ))}
    </div>
  )
}

/* ==========================================================================
 * Trend charts
 * ======================================================================== */

export interface SeriesPoint {
  label: string
  [key: string]: string | number
}

export interface TrendChartProps {
  data: SeriesPoint[]
  /** Keys to plot, in order. The first is the primary series. */
  series: Array<{ key: string; name: string; tone?: 'accent' | 'success' | 'warning' | 'danger' | 'info' }>
  height?: number
  valueSuffix?: string
  className?: string
}

/**
 * Area trend.
 *
 * The first series is painted in the neutral accent; later ones only take a
 * functional colour when that colour carries meaning. Everything else stays
 * monochrome, so colour keeps signalling rather than decorating.
 */
export function AreaTrendChart({ data, series, valueSuffix, className }: TrendChartProps) {
  const { tokens, axis, grid } = useAxisProps()

  const colourFor = (tone: string | undefined, index: number) => {
    if (index === 0 && !tone) return tokens.accent
    switch (tone) {
      case 'success':
        return tokens.success
      case 'warning':
        return tokens.warning
      case 'danger':
        return tokens.danger
      case 'info':
        return tokens.info
      default:
        return index === 0 ? tokens.accent : tokens.subtle
    }
  }

  return (
    <AreaChart data={data} className={className} accessibilityLayer>
      <defs>
        {series.map((item, index) => (
          <linearGradient key={item.key} id={`area-${item.key}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colourFor(item.tone, index)} stopOpacity={0.28} />
            <stop offset="100%" stopColor={colourFor(item.tone, index)} stopOpacity={0.02} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid {...grid} />
      <XAxis dataKey="label" {...axis} />
      <YAxis {...axis} width={48} />
      <Tooltip
        content={<ChartTooltip tokens={tokens} suffix={valueSuffix} />}
        cursor={{ stroke: tokens.border }}
      />
      {series.map((item, index) => (
        <Area
          key={item.key}
          type="monotone"
          dataKey={item.key}
          name={item.name}
          stroke={colourFor(item.tone, index)}
          strokeWidth={2}
          fill={`url(#area-${item.key})`}
          dot={false}
          activeDot={{ r: 3 }}
        />
      ))}
    </AreaChart>
  )
}

export function LineTrendChart({ data, series, valueSuffix, className }: TrendChartProps) {
  const { tokens, axis, grid } = useAxisProps()

  return (
    <LineChart data={data} className={className} accessibilityLayer>
      <CartesianGrid {...grid} />
      <XAxis dataKey="label" {...axis} />
      <YAxis {...axis} width={48} />
      <Tooltip
        content={<ChartTooltip tokens={tokens} suffix={valueSuffix} />}
        cursor={{ stroke: tokens.border }}
      />
      {series.map((item, index) => (
        <Line
          key={item.key}
          type="monotone"
          dataKey={item.key}
          name={item.name}
          stroke={index === 0 ? tokens.accent : tokens.subtle}
          strokeWidth={2}
          strokeDasharray={index === 0 ? undefined : '4 4'}
          dot={false}
        />
      ))}
    </LineChart>
  )
}

export interface BarSeriesChartProps extends TrendChartProps {
  /** Stack the series instead of placing them side by side. */
  stacked?: boolean
}

export function BarSeriesChart({
  data,
  series,
  valueSuffix,
  stacked = false,
  className,
}: BarSeriesChartProps) {
  const { tokens, axis, grid } = useAxisProps()

  const colourFor = (tone: string | undefined, index: number) => {
    switch (tone) {
      case 'success':
        return tokens.success
      case 'warning':
        return tokens.warning
      case 'danger':
        return tokens.danger
      case 'info':
        return tokens.info
      default:
        return index === 0 ? tokens.accent : tokens.subtle
    }
  }

  return (
    <BarChart data={data} className={className} accessibilityLayer>
      <CartesianGrid {...grid} />
      <XAxis dataKey="label" {...axis} />
      <YAxis {...axis} width={48} />
      <Tooltip
        content={<ChartTooltip tokens={tokens} suffix={valueSuffix} />}
        cursor={{ fill: tokens.grid, fillOpacity: 0.4 }}
      />
      {series.map((item, index) => (
        <Bar
          key={item.key}
          dataKey={item.key}
          name={item.name}
          stackId={stacked ? 'stack' : undefined}
          fill={colourFor(item.tone, index)}
          radius={stacked ? 0 : [4, 4, 0, 0]}
          maxBarSize={40}
        />
      ))}
    </BarChart>
  )
}

/**
 * Bucketed distribution — latency histograms and similar.
 * Bars are tinted by position, so slow buckets read as slow at a glance.
 */
export interface HistogramChartProps {
  data: Array<{ label: string; count: number; tone?: 'success' | 'warning' | 'danger' }>
  valueSuffix?: string
  className?: string
}

export function HistogramChart({ data, valueSuffix, className }: HistogramChartProps) {
  const { tokens, axis, grid } = useAxisProps()

  return (
    <BarChart data={data} className={className} accessibilityLayer>
      <CartesianGrid {...grid} />
      <XAxis dataKey="label" {...axis} />
      <YAxis {...axis} width={48} />
      <Tooltip
        content={<ChartTooltip tokens={tokens} suffix={valueSuffix} />}
        cursor={{ fill: tokens.grid, fillOpacity: 0.4 }}
      />
      <Bar dataKey="count" name="Queries" radius={[4, 4, 0, 0]} maxBarSize={48}>
        {data.map((entry, index) => (
          <Cell
            key={index}
            fill={
              entry.tone === 'danger'
                ? tokens.danger
                : entry.tone === 'warning'
                  ? tokens.warning
                  : entry.tone === 'success'
                    ? tokens.success
                    : tokens.accent
            }
          />
        ))}
      </Bar>
    </BarChart>
  )
}

/* ==========================================================================
 * DonutChart
 * ======================================================================== */

export interface DonutSlice {
  name: string
  value: number
  tone?: 'accent' | 'success' | 'warning' | 'danger' | 'info'
}

export interface DonutChartProps {
  data: DonutSlice[]
  /** Formats the centred total. */
  formatTotal?: (total: number) => string
  className?: string
}

export function DonutChart({ data, formatTotal, className }: DonutChartProps) {
  const { tokens } = useAxisProps()
  const total = data.reduce((sum, slice) => sum + slice.value, 0)

  const colourFor = (tone: DonutSlice['tone'], index: number) => {
    switch (tone) {
      case 'success':
        return tokens.success
      case 'warning':
        return tokens.warning
      case 'danger':
        return tokens.danger
      case 'info':
        return tokens.info
      case 'accent':
        return tokens.accent
      default:
        // Later slices fall back to progressively lighter greys.
        return index % 2 === 0 ? tokens.accent : tokens.subtle
    }
  }

  return (
    <div className={cn('relative h-full w-full', className)}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart accessibilityLayer>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius="62%"
            outerRadius="88%"
            paddingAngle={2}
            stroke="none"
          >
            {data.map((slice, index) => (
              <Cell key={slice.name} fill={colourFor(slice.tone, index)} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip tokens={tokens} />} />
        </PieChart>
      </ResponsiveContainer>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-lg font-semibold text-foreground">
          {formatTotal ? formatTotal(total) : total}
        </span>
      </div>
    </div>
  )
}

/* ==========================================================================
 * Sparkline
 * ======================================================================== */

export interface SparklineProps {
  data: number[]
  /** Paints the line in a functional colour instead of the neutral accent. */
  tone?: 'accent' | 'success' | 'warning' | 'danger'
  width?: number
  height?: number
  className?: string
}

/** No axes, no tooltip — a shape, for use inside a StatCard. */
export function Sparkline({
  data,
  tone = 'accent',
  width = 88,
  height = 32,
  className,
}: SparklineProps) {
  const { tokens } = useAxisProps()

  const colour = {
    accent: tokens.accent,
    success: tokens.success,
    warning: tokens.warning,
    danger: tokens.danger,
  }[tone]

  const points = data.map((value, index) => ({ index, value }))
  const gradientId = `spark-${tone}`

  return (
    <div className={className} style={{ width, height }} aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colour} stopOpacity={0.3} />
              <stop offset="100%" stopColor={colour} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="value"
            stroke={colour}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
